import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { AUTOMATION_STRATEGIES, VALID_SPIDER_EXCLUSION_VALUES, LINE_SEGMENT_SPIDER } from '../helpers/constants';
import { automationStationApi } from '../api';

const kobeZonePricingSchema = z.object({
  zone: z.string(),
  floor: z
    .number()
    .positive()
    .nullable(),
  ceiling: z
    .number()
    .positive()
    .nullable(),
});

const baseSchema = z.object({
  automation_strategy: z.string(),
  is_kobe_eligible: z.boolean(),
  zone_kobe_eligible: z.boolean(),
  reason: z
    .string()
    .trim()
    .nonempty()
    .nullable(),
  spider_exclusion: z
    .enum(VALID_SPIDER_EXCLUSION_VALUES)
    .nullable()
    .optional()
    .catch(undefined),
  line_segment_expectations: z
    .object({
      lse_dte_60: z
        .number()
        .nullable()
        .optional(),
      lse_dte_30: z
        .number()
        .nullable()
        .optional(),
      lse_dte_15: z
        .number()
        .nullable()
        .optional(),
      lse_dte_7: z
        .number()
        .nullable()
        .optional(),
      lse_dte_4: z
        .number()
        .nullable()
        .optional(),
      lse_sellout_dte: z
        .number()
        .nullable()
        .optional(),
      lse_sellout_dte_target: z
        .number()
        .nullable()
        .optional(),
    })
    .nullable()
    .optional(),
});

const spiderSchema = baseSchema.extend({
  automation_strategy: z.literal(AUTOMATION_STRATEGIES.SPIDER.key),
  spider_exclusion: z.enum(VALID_SPIDER_EXCLUSION_VALUES).nullable(),
});

const lineSegmentSpiderSchema = baseSchema.extend({
  automation_strategy: z.literal(LINE_SEGMENT_SPIDER),
  spider_exclusion: z.enum(VALID_SPIDER_EXCLUSION_VALUES).nullable(),
});

const kobeSchema = baseSchema.extend({
  automation_strategy: z.literal(AUTOMATION_STRATEGIES.KOBE.key),
  kobe_included_proxies: z.array(z.number().positive()).catch([]),
  kobe_excluded_proxies: z.array(z.number().positive()).catch([]),
  kobe_zones: z.array(kobeZonePricingSchema).catch([]),
});

const KobeZoneProxyEvents = z.array(z.number());

const kobeZoneSchema = baseSchema.extend({
  automation_strategy: z.literal(AUTOMATION_STRATEGIES.KOBE_ZONE.key),
  zone_kobe_included_proxies: z.record(z.string(), KobeZoneProxyEvents).catch({}),
  zone_kobe_excluded_proxies: z.record(z.string(), KobeZoneProxyEvents).catch({}),
  kobe_zones: z.array(kobeZonePricingSchema).catch([]),
});

const proxyEventSchema = z.object({
  lobsang_event_id: z.number(),
  event_date: z.string(),
  performer: z.string(),
  proxy_zone: z.string().optional(),
  rank: z.number(),
  similarity: z.number(),
  venue: z.string(),
  zone: z.string().optional(),
});

const automationSchema = z.object({
  current_strategy: z
    .union([
      z.discriminatedUnion('automation_strategy', [spiderSchema, kobeSchema, kobeZoneSchema, lineSegmentSpiderSchema]),
      baseSchema,
    ])
    .nullable()
    .optional(),
  proxy_events: z
    .object({
      KOBE: z.array(proxyEventSchema).catch([]),
      KOBE_ZONE: z.record(z.array(proxyEventSchema)).catch({}),
    })
    .nullable()
    .optional(),
});

async function fetchAutomation(lobsangEventId) {
  const url = `v1/automation/${lobsangEventId}`;
  const { data } = await automationStationApi.get(url);
  return automationSchema.parse(data);
}

export function useAutomation(lobsangEventId) {
  const query = useQuery({
    queryKey: ['automation', lobsangEventId],
    queryFn: () => fetchAutomation(lobsangEventId),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
  return query;
}
